/**
 * Pure mapping helper: LiveKit VoiceState → handoff visuals (FR-014, FR-016).
 *
 * No React, no LiveKit runtime — import-safe on the critical path.
 * See research R2 and data-model.md VoiceVisualState for the full mapping table.
 */

import type { VoiceState } from '@/hooks/v2/useVoiceConversation';
import type { VoiceEndReason } from '@shared/livekit-constants';

export interface VoiceVisualState {
  /** Bar layout ring — 'ready' (pre-connect/idle) or 'listening' (active session). */
  layout: 'ready' | 'listening';
  /** Waveform animation preset for AnakaBarVisualizerView. */
  waveform: 'baseline' | 'thinking' | 'responding';
  /** Whether the VoiceSessionTimer should be shown. */
  timerVisible: boolean;
  /** Human-readable status label shown in the input bar. */
  statusLabel: string;
  /** Hex accent colour for the status dot/label. Default '#A855F7' (purple). */
  statusAccent: string;
}

/**
 * Map the live LiveKit VoiceState to the VoiceVisualState view model.
 *
 * Pure, synchronous, zero side-effects. All callers (ConversationInputBar,
 * AnakaBarVisualizerView wrappers, PeekHint) derive their visual state from
 * this single source of truth.
 *
 * @example
 *   const visual = mapVoiceVisualState(voiceState);
 *   // visual.waveform → pass to AnakaBarVisualizerView state prop
 *   // visual.layout   → switch between VoiceInline / VoiceExpanded renders
 */
export function mapVoiceVisualState(
  voiceState: VoiceState,
  /**
   * Optional custom status label pushed by n8n (spec 015 — US4), shown in place
   * of the default "Thinking" label while the agent is thinking.
   */
  statusLabelOverride?: string | null,
  /**
   * ANA-407 — true while `useVoiceConversation`'s idle "still there?" prompt is
   * showing (phase 1 of the two-phase idle auto-pause, v1.0.4). Only meaningful
   * while `listening` — overrides the label so the user knows a pause is
   * imminent instead of reading the neutral "Listening".
   */
  idlePrompting?: boolean,
  /**
   * ANA-575 — why the agent ended the call, when `voiceState === 'ended'`. Drives
   * reason-aware close copy (e.g. `cap` → "Time limit reached"). Ignored for
   * every other state.
   */
  endedReason?: VoiceEndReason | null,
): VoiceVisualState {
  switch (voiceState) {
    case 'idle':
      return {
        layout: 'ready',
        waveform: 'baseline',
        timerVisible: false,
        statusLabel: 'Ready when you are',
        statusAccent: '#22C55E',
      };

    // Requesting the session token (`POST /voice/token`) — the very first step.
    case 'authorizing':
      return {
        layout: 'ready',
        waveform: 'baseline',
        timerVisible: false,
        statusLabel: 'Starting session',
        statusAccent: '#A855F7',
      };

    case 'connecting':
      return {
        layout: 'ready',
        waveform: 'baseline',
        timerVisible: false,
        statusLabel: 'Connecting',
        statusAccent: '#8B5CF6',
      };

    // Room is joined but Anaka hasn't spoken yet — keep a loading look until her
    // greeting audio starts (then the hook flips to 'listening'). The live
    // chrome is active so the agent audio renders, but the surface reads as
    // "getting ready", not "ready".
    // Room joined; waiting for the agent to spin up (cold start) and compose +
    // synthesise the greeting. The longest phase on a first/cold session.
    case 'preparing':
      return {
        layout: 'listening',
        waveform: 'thinking',
        timerVisible: false,
        statusLabel: 'Getting ready',
        statusAccent: '#6366F1',
      };

    case 'listening':
      return idlePrompting
        ? {
            layout: 'listening',
            waveform: 'thinking',
            timerVisible: true,
            statusLabel: "Still there? I'll pause shortly",
            statusAccent: '#F59E0B',
          }
        : {
            layout: 'listening',
            waveform: 'thinking',
            timerVisible: true,
            statusLabel: 'Listening',
            statusAccent: '#22C55E',
          };

    case 'thinking':
      return {
        layout: 'listening',
        waveform: 'thinking',
        timerVisible: true,
        // US4: a custom n8n status_label overrides the default "Thinking".
        statusLabel: statusLabelOverride || 'Thinking',
        statusAccent: '#60A5FA',
      };

    case 'speaking':
      return {
        layout: 'listening',
        waveform: 'responding',
        timerVisible: true,
        statusLabel: 'Responding',
        statusAccent: '#F59E0B',
      };

    // Mid-session connection recovery (network blip, or resume after a pause).
    // Keep the active look + timer so it reads as "recovering", not "ended".
    case 'reconnecting':
      return {
        layout: 'listening',
        waveform: 'thinking',
        timerVisible: true,
        statusLabel: 'Reconnecting',
        statusAccent: '#F59E0B',
      };

    case 'disconnected':
      return {
        layout: 'ready',
        waveform: 'baseline',
        timerVisible: false,
        statusLabel: 'Ready when you are',
        statusAccent: '#22C55E',
      };

    // ANA-575 — the agent INTENTIONALLY ended the call (goodbye / scope-lock /
    // cap / guardrail). Terminal, and deliberately NOT the red error accent: an
    // intentional close is not a failure. Slate reads as "done", distinct from
    // idle/disconnected green ("ready") and error red.
    case 'ended':
      return {
        layout: 'ready',
        waveform: 'baseline',
        timerVisible: false,
        statusLabel: endedReason === 'cap' ? 'Time limit reached' : 'Anaka ended the call',
        statusAccent: '#94A3B8',
      };

    case 'error':
      return {
        layout: 'ready',
        waveform: 'baseline',
        timerVisible: false,
        statusLabel: 'Voice error',
        statusAccent: '#EF4444',
      };
  }
}
